import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { MeetingRoutingRuleApiService } from '../../services/meeting-routing-rule-api.service';
import { MeetingRoutingRuleDrawerService } from '../../services/meeting-routing-rule-drawer.service';
import { MeetingRoutingRuleResponse, ROUTING_RULE_TYPE_LABELS, meetingLabel } from '../../models/meeting.model';
import { MeetingBookingLinkApiService } from '../../services/meeting-booking-link-api.service';
import { UserApiService } from '../../../users/services/user-api.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-config-routing-rules-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './routing-rules-list.component.html',
})
export class RoutingRulesListComponent implements OnInit, OnDestroy {
  items: MeetingRoutingRuleResponse[] = [];
  loading = false;
  error = '';

  private readonly destroy$ = new Subject<void>();

  /** id → título legible; evita pintar «#7» en la columna «Página de reserva». */
  private readonly bookingLinkNames = new Map<number, string>();
  /** id → nombre del usuario destino; evita pintar «User #12». */
  private readonly userNames = new Map<number, string>();

  constructor(
    private api: MeetingRoutingRuleApiService,
    private bookingLinkApi: MeetingBookingLinkApiService,
    private userApi: UserApiService,
    private drawerService: MeetingRoutingRuleDrawerService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ruleTypeLabel(type: string | null | undefined): string {
    return meetingLabel(ROUTING_RULE_TYPE_LABELS, type);
  }

  bookingLinkName(id: number | null | undefined): string {
    if (!id) return '';
    return this.bookingLinkNames.get(id) ?? '';
  }

  userTargetName(id: number | null | undefined): string {
    if (!id) return '';
    return this.userNames.get(id) ?? '';
  }

  private loadCatalogs(): void {
    const businessId = this.businessId;
    this.bookingLinkApi.filterActiveLinks(businessId).subscribe({
      next: links => {
        this.bookingLinkNames.clear();
        for (const l of links) this.bookingLinkNames.set(l.id, l.title);
      },
      error: () => { /* sin catálogo se muestra «—»; nunca un id desnudo */ }
    });
    this.userApi.listActiveByBusiness(businessId).subscribe({
      next: users => {
        this.userNames.clear();
        for (const u of users) {
          this.userNames.set(u.id, `${u.fullname ?? ''} ${u.lastname ?? ''}`.trim() || u.mail);
        }
      },
      error: () => { /* idem */ }
    });
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.load();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  load(): void {
    this.loading = true;
    this.api.list(this.businessId, 0, 200).subscribe({
      next: (page) => {
        this.items = [...page.content].sort(
          (a, b) => (a.priorityOrder ?? 0) - (b.priorityOrder ?? 0)
        );
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando reglas';
        this.loading = false;
      },
    });
  }

  crear(): void {
    this.drawerService.open();
  }

  editar(item: MeetingRoutingRuleResponse): void {
    this.drawerService.open({ ruleId: item.id });
  }

  editarPagina(item: MeetingRoutingRuleResponse): void {
    this.router.navigate(['/meeting/config/routing-rules/editar', item.id]);
  }

  async eliminar(item: MeetingRoutingRuleResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla',
      message: `¿Eliminar regla "${item.ruleName}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.load(); this.toast.deleted('Regla de enrutamiento', `Se eliminó "${item.ruleName}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }
}
